import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Briefcase, Loader2, XCircle, Globe, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminBusinessAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getBusinessAccounts({ page, limit: 20 });
      setAccounts(data.users || []);
      setTotalPages(data.pages || 1);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [page]);

  const handleRevoke = async (userId, name) => {
    if (!window.confirm(`Revoke business account for ${name}?`)) return;
    try {
      await adminAPI.revokeBusinessAccount(userId);
      toast.success('Business account revoked');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-business-accounts">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Business Accounts</h1>
        <p className="text-sm text-zinc-500 mt-1">Manage business account upgrades</p>
      </div>

      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden" data-testid="business-table">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 text-[#002FA7] animate-spin mx-auto" /></div>
        ) : accounts.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-400">No business accounts yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100 bg-zinc-50/50">
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Company</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Owner</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Contact</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Upgraded</th>
                  <th className="text-right px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map(u => (
                  <tr key={u.user_id} className="border-b border-zinc-50 hover:bg-zinc-50/50" data-testid={`biz-row-${u.user_id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-[#002FA7] flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-zinc-900">{u.company_name}</p>
                          {u.business_description && <p className="text-xs text-zinc-400 line-clamp-1 max-w-xs">{u.business_description}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-zinc-900">{u.name || u.email}</p>
                      <p className="text-xs text-zinc-400">{u.email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1 text-xs text-zinc-500">
                        {u.website && <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {u.website}</span>}
                        {u.business_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {u.business_phone}</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs text-zinc-500">{u.business_upgraded_at ? new Date(u.business_upgraded_at).toLocaleDateString() : '-'}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => handleRevoke(u.user_id, u.company_name || u.name)}
                        className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-red-600 border border-red-200 hover:bg-red-50"
                        data-testid={`revoke-biz-${u.user_id}`}
                      ><XCircle className="w-3 h-3 inline mr-1" />Revoke</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-zinc-100">
            <p className="text-xs text-zinc-500">Page {page} of {totalPages}</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="p-1.5 rounded-lg border border-zinc-200 text-zinc-400 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="p-1.5 rounded-lg border border-zinc-200 text-zinc-400 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
